import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";

import {
  Container,
  Dialog,
  Modal,
  Pressable,
  Row,
  useToast,
} from "../components";
import { PulseLoader } from "../components/pulse_loader";
import { useMediaQuery } from "../hooks";
import { FirstDayOfWeek, toDay } from "../lib/datetime";
import {
  invalidateMultiPassAccessCache,
  useAccessAddOnQuery,
  useCancelMultiPassBooking,
  useCreateMultiPassBooking,
  usePartnerQuery,
  useSpaceV2Query,
} from "./api";
import { BackButton } from "./back_button";
import { Button } from "./button";
import { CloseButton } from "./close_button";
import { Content } from "./content";
import { DatePicker } from "./date_picker";
import { getLocalToday } from "./date_utils";
import { isAfter, isBefore } from "./day_utils";
import { Divider } from "./divider";
import { Heading } from "./heading";
import { displayBookingDate } from "./multipass_access";
import { MultiPassCard } from "./multipass_card";
import { useMultiPassOrderHelpers } from "./order_params";
import { PartnerPortalFooter } from "./partner_portal_footer";
import { PortalHead, PortalHeader } from "./template_partner_portal_index";
import { SecondaryButton } from "./secondary_button";
import { Spacer } from "./spacer";
import { Text } from "./text";
import { PartnerConfig } from "./partner_config";

interface TemplatePartnerPortalAccessParams {
  multiPassID: string;
}

interface TemplatePartnerPortalAccessProps {
  partnerConfig: PartnerConfig;
}

export function TemplatePartnerPortalAccess(
  props: TemplatePartnerPortalAccessProps
) {
  const { partnerConfig } = props;
  const {
    slug,
    partnerName,
    favicon,
    homepageURL,
    firstDayOfWeek,
    portalHelpEmail,
  } = partnerConfig;
  const history = useHistory();
  const pageParams = useParams<TemplatePartnerPortalAccessParams>();
  const partner = usePartnerQuery(slug);

  return (
    <div>
      <PortalHead title={partnerName} favicon={favicon} />
      <PortalHeader homepageURL={homepageURL} logo={partner.logoUrl} />
      <Divider />
      <Spacer size={32} />
      <Content>
        <div className="partner-portal-expand-content">
          <Container paddingHorizontal={16}>
            <Spacer size={24} />
            <BackButton onPress={() => history.replace(`/portal/${slug}`)} />
            <Spacer size={24} />
            <MultiPassDetails
              partnerID={partner.id}
              multiPassID={pageParams.multiPassID}
              firstDayOfWeek={firstDayOfWeek}
            />
            <Spacer size={80} />
          </Container>
        </div>
        <Spacer size={32} />
      </Content>
      <PartnerPortalFooter portalHelpEmail={portalHelpEmail} />
    </div>
  );
}

interface MultiPassDetailsProps {
  partnerID: string;
  multiPassID: string;
  firstDayOfWeek?: FirstDayOfWeek;
}

function MultiPassDetails(props: MultiPassDetailsProps) {
  const { multiPassID, partnerID, firstDayOfWeek } = props;
  const toast = useToast();
  const multiPassAccess = useAccessAddOnQuery(partnerID, multiPassID);
  const space = useSpaceV2Query(multiPassAccess.space.id);
  const createMultiPassBooking = useCreateMultiPassBooking();
  const cancelMultiPassBooking = useCancelMultiPassBooking();
  const [creatingNewBooking, setCreatingNewBooking] = useState(false);
  const [canceledBooking, setCanceledBooking] = useState<string | null>(null);

  const handleNewBooking = useCallback(
    async (date?: Date) => {
      if (!date) {
        return;
      }
      setCreatingNewBooking(true);
      await createMultiPassBooking(partnerID, multiPassAccess.id, {
        date: toDay(date),
      });
      await invalidateMultiPassAccessCache(multiPassAccess.id);
      setCreatingNewBooking(false);
    },
    [multiPassAccess.id, partnerID, createMultiPassBooking]
  );

  const handleCancelBooking = useCallback(
    async (bookingID: string) => {
      try {
        setCanceledBooking(bookingID);
        await cancelMultiPassBooking(bookingID);
      } catch (error) {
        console.error(error);
        toast.error({ message: "Could not cancel booking " });
      } finally {
        await invalidateMultiPassAccessCache(multiPassAccess.id);
        setTimeout(() => {
          setCanceledBooking(null);
        }, 300);
      }
    },
    [multiPassAccess.id, cancelMultiPassBooking, toast]
  );

  let displayedDateRowContents = multiPassAccess.bookings
    .sort((a, b) => {
      if (isBefore(a.date, b.date)) {
        return 1;
      } else if (isAfter(a.date, b.date)) {
        return -1;
      }

      return 0;
    })
    .map((b) => {
      const localToday = getLocalToday(space.locationTimeZone);
      const today = localToday === b.date;

      return (
        <Container>
          {b.id === canceledBooking ? (
            <PulseLoader />
          ) : (
            <Row>
              <Container flex={1}>
                <Text color={b.past ? "muted" : "default"}>
                  {displayBookingDate(b.date, undefined, "short")}
                </Text>
              </Container>
              {!b.past && !today && (
                <Pressable onPress={() => handleCancelBooking(b.id)}>
                  <Text decoration="underline" color="link">
                    Cancel
                  </Text>
                </Pressable>
              )}
            </Row>
          )}
        </Container>
      );
    });

  if (creatingNewBooking) {
    displayedDateRowContents.unshift(<PulseLoader />);
  }

  return (
    <Container>
      <MultiPassCard readOnly multiPassAccess={multiPassAccess} />
      <Spacer size={8} />
      <Text>
        Expires on: {displayBookingDate(multiPassAccess.expirationDate)}
      </Text>
      <Spacer size={32} />
      <Row justifyContent="space-between">
        <Heading>Bookings</Heading>
        {!multiPassAccess.expired &&
          multiPassAccess.bookingsUnusedCount > 0 && (
            <NewBooking
              partnerID={partnerID}
              onNewBooking={handleNewBooking}
              multiPassID={multiPassAccess.id}
              firstDayOfWeek={firstDayOfWeek}
              creatingNewBooking={creatingNewBooking}
            />
          )}
      </Row>
      <Spacer size={16} />
      {displayedDateRowContents.map((rowContent, i) => (
        <Container
          borderColor="#F5F6F7"
          borderBottomWidth={1}
          paddingTop={12}
          paddingBottom={12}
          key={i}
        >
          {rowContent}
        </Container>
      ))}
    </Container>
  );
}

interface NewBookingProps {
  multiPassID: string;
  partnerID: string;
  onNewBooking: (date?: Date) => void;
  creatingNewBooking: boolean;
  firstDayOfWeek?: FirstDayOfWeek;
}

function NewBooking(props: NewBookingProps) {
  const {
    onNewBooking,
    firstDayOfWeek,
    partnerID,
    multiPassID,
    creatingNewBooking,
  } = props;
  const [open, setOpen] = useState(false);
  const mq = useMediaQuery();

  const { isBlockedDate, isOutsideRange } = useMultiPassOrderHelpers(
    partnerID,
    multiPassID
  );

  return (
    <Container>
      <SecondaryButton
        onPress={() => setOpen(true)}
        icon="calendar"
        title="New"
        disabled={creatingNewBooking}
      />
      <BookingDatePicker
        open={open}
        firstDayOfWeek={firstDayOfWeek}
        onClose={() => setOpen(false)}
        onComplete={onNewBooking}
        onNextVisibleDates={() => {}}
        view={
          mq.device === "tablet" || mq.device === "desktop" ? "dialog" : "modal"
        }
        ctaTitle="Book"
        isOutsideRange={isOutsideRange}
        isBlockedDate={isBlockedDate}
      />
    </Container>
  );
}

interface BookingDatePickerProps {
  value?: Date;
  onComplete: (date?: Date) => void;
  ctaTitle: string;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  view: "modal" | "dialog";
  open: boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  onClose: () => void;
}

export function BookingDatePicker(props: BookingDatePickerProps) {
  const {
    value,
    open,
    onComplete,
    onClose,
    view,
    onNextVisibleDates,
    isOutsideRange,
    ctaTitle,
    firstDayOfWeek,
    isBlockedDate,
  } = props;
  const [internalValue, setInternalValue] = useState(value);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleChange = useCallback((value) => {
    setInternalValue(value);
  }, []);

  const handleSubmit = useCallback(() => {
    setInternalValue(undefined);
    onClose();
    onComplete(internalValue);
  }, [onClose, onComplete, internalValue]);

  return (
    <Fragment>
      {view === "modal" ? (
        <Modal isOpen={open} onRequestClose={onClose}>
          <Container expanded paddingHorizontal={16} paddingBottom={16}>
            <Row>
              <CloseButton onPress={onClose} />
            </Row>
            <div style={{ flex: 1, overflow: "auto" }}>
              <DatePicker
                firstDayOfWeek={firstDayOfWeek}
                value={internalValue}
                onChange={handleChange}
                isBlocked={isBlockedDate}
                isOutsideRange={isOutsideRange}
                onNextVisibleDates={onNextVisibleDates}
                numberOfMonths={2}
                orientation="vertical"
              />
            </div>
            <Spacer size={16} />
            <Container>
              <Button size="lg" onPress={handleSubmit} title={ctaTitle} />
            </Container>
          </Container>
        </Modal>
      ) : (
        <Dialog isOpen={open} onRequestClose={onClose}>
          <Container width={700} padding={16}>
            <DatePicker
              firstDayOfWeek={firstDayOfWeek}
              onChange={handleChange}
              value={internalValue}
              isBlocked={isBlockedDate}
              isOutsideRange={isOutsideRange}
              onNextVisibleDates={onNextVisibleDates}
              numberOfMonths={2}
              orientation="horizontal"
            />
            <Row justifyContent="flex-end">
              <Button
                disabled={!internalValue}
                onPress={handleSubmit}
                title={ctaTitle}
                override={{
                  button: { style: { paddingLeft: 40, paddingRight: 40 } },
                }}
              />
            </Row>
          </Container>
        </Dialog>
      )}
    </Fragment>
  );
}
